import { FC, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { openModal } from 'store/actions/modals'
import { useUpdateSettings, useSelectedPreset } from './hooks'
import { FilterDropDown } from '.'
import { MFilters } from 'types/filterTypes'

import { t } from 'translations'
import { Badge } from 'ui'

type SavedPresetsProps = {
  contentName: MFilters.ContentTypes
}

const SavedPresets: FC<SavedPresetsProps> = ({ contentName }) => {
  const dispatch = useDispatch()
  const presetKey: MFilters.PresetKey = `saved-search-${contentName}`
  const filterKey: MFilters.FilterKey = `filter-${contentName}`

  const settings = useSelector((state: MStore.RootState) => state.settings)
  const savedOptions = useMemo(() => {
    return settings[filterKey] || {}
  }, [settings, filterKey])
  const presets: MFilters.PresetTypes<typeof filterKey> = useMemo(() => {
    return settings[presetKey] || {}
  }, [settings, presetKey])

  const presetsKeysArray = Object.keys(presets || {})

  const selectedPreset = useSelectedPreset(presets, savedOptions)
  const { saveOptions } = useUpdateSettings(contentName)

  const hasDateRange = ('rangeStart' in savedOptions && !!savedOptions.rangeStart) || ('rangeEnd' in savedOptions && !!savedOptions.rangeEnd)
  const hasFilters = Object.values(savedOptions).some(value => Array.isArray(value) && value.length > 0) || hasDateRange

  return (
    <FilterDropDown
      buttonContent={
        <>
          <div className="d-flex flex-row justify-content-between align-items-baseline ">
            <div className="d-flex  flex-row align-items-baseline">
              {presetsKeysArray.length ? (
                <Badge variant="light" className=" mr-1">
                  {presetsKeysArray.length}
                </Badge>
              ) : null}
            </div>

            <i className={`fa fa-caret-down mx-1`} />
          </div>
        </>
      }
      splitButton={
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={!hasFilters}
          onClick={() => dispatch(openModal('SaveSearchModal', { contentName, savedOptions }))}
        >
          <i className="fas fa-save"></i>
        </button>
      }
      buttonClass="btn btn-secondary btn-sm rounded-left border-0 pl-2 mr-0 "
      contentName={contentName}
      dropDownType="SavedPresets"
    >
      {() => (
        <div className="d-flex flex-column dropdown-menu">
          {presetsKeysArray.length ? (
            presetsKeysArray.map((presetName, index) => {
              const optionsToSave = presetName === selectedPreset ? { rangeStart: undefined, rangeEnd: undefined } : presets[presetName]
              return (
                <button
                  type="button"
                  key={index}
                  className={`dropdown-item d-flex justify-content-between ${presetName === selectedPreset ? 'active' : ''}`}
                  onClick={() => {
                    saveOptions(optionsToSave)
                  }}
                >
                  <div className="mr-3">{presetName}</div>
                  <span
                    className="pointer-cursor"
                    onClick={e => {
                      e.stopPropagation()
                      dispatch(openModal('DeleteSearchPresetConfirmationModal', { contentName, presetName, selectedPreset }))
                    }}
                  >
                    <i className="fas fa-trash fa-sm" />
                  </span>
                </button>
              )
            })
          ) : (
            <div className="d-flex align-items-baseline mx-3 text-nowrap">
              <i className="far fa-empty-set mr-2"></i>
              {t('searchBar.NO_SAVED_PRESETS')}
            </div>
          )}
        </div>
      )}
    </FilterDropDown>
  )
}

export default SavedPresets
